<script setup>
import {reactive, onMounted, onUnmounted} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import ManageFederatedGroupComponent from "@/Components/ManageRoles/ManageFederatedGroupComponent.vue";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'FederatedGroupsComponent';

const data = reactive({
  dialog: {
    id: 0,
    status: "closed",
    updateNum: 0,
    create: false,
    update: false
  },
  dataLoading: true,
  loading: false,
  groups: [],
  headers: [
    {
      title: 'Name',
      align: 'start',
      sortable: true,
      key: 'name',
    },
    { title: 'Federated ID', key: 'group_id', sortable: true },
    { title: 'Admin Group', key: 'is_admin', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false, align: 'end' },
  ],
});

const listAll = () => {
  data.dataLoading = true;
  ApiWrapper.get('federated-groups', 'list-groups').then(async (response) => {
    data.groups = response.groups;
  })
      .finally(() => {
        data.dataLoading = false;
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
}

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this federated group?'
  )
  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('federated-groups', 'delete-group', { id: item.id })
        .then((response) => {
          listAll();
          ToastUtil.successMsg('Deleted federated group');
        })
        .catch((e) => {
          ToastUtil.error(e);
        })
        .finally(() => {
          data.loading = false;
        })
  }
}

const editItem = (item) => {
  data.dialog.id = item.id;
  data.dialog.create = false;
  data.dialog.update = true;
  data.dialog.updateNum++;
  data.dialog.status = "open";
}

const createItem = () => {
  data.dialog.id = 0;
  data.dialog.create = true;
  data.dialog.update = false;
  data.dialog.updateNum++;
  data.dialog.status = "open";
}

onMounted(()=>{
  listAll();
});

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});

</script>

<template>
  <v-container fluid>
    <ManageFederatedGroupComponent
        v-if="Permissions.checkPermissions([],true)"
        v-show="data.dialog.status === 'open'"
        :federatedGroupId="data.dialog.id"
        :status="data.dialog.status"
        :updateNum="data.dialog.updateNum"
        :create="data.dialog.create"
        :update="data.dialog.update"
        @closed="data.dialog.status='closed'"
        @update="listAll()"
    />

    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Federated Groups</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>

        <v-spacer></v-spacer>

        <!-- New Federated Group Button -->
        <v-btn
            v-if="Permissions.checkPermissions([],true)"
            class="primary"
            size="small"
            @click="createItem()"
        >
          New Federated Group
        </v-btn>
      </v-toolbar>

      <v-data-table
          :headers="data.headers"
          :items="data.groups"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <span>
                {{ item.name }}
              </span>
            </td>
            <td>
              <v-tooltip :text="'Not mapped to federated id'" v-if="!item.group_id">
                <template v-slot:activator="{ props }">
                  <v-icon class="mr-2" color="orange" v-bind="props"> mdi-alert </v-icon>
                </template>
              </v-tooltip>

              {{ item.group_id }}
            </td>
            <td>
              <v-icon class="mr-2" v-if="item.is_admin"> mdi-check </v-icon>
            </td>
            <td>
              <span class="float-right">
                <v-icon v-if="Permissions.checkPermissions([],true)" @click="editItem(item)"> mdi-pencil </v-icon>
                <v-icon v-if="Permissions.checkPermissions([],true)" class="mr-2" @click="deleteItem(item)"> mdi-delete </v-icon>
              </span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}
</style>